/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * <p>
 * Copyright © 2019 Pearson Education, Inc.
 * All Rights Reserved.
 * <p>
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained from Pearson Education, Inc.
 *
 * This is the data class for holding partner attributes.
 *
 * @author urahish
 * 01/24/2022
 *
 */

import React from 'react';
import '../../form-elements/formElement.css';
import API from "../../../../../utils/api";
import EventEmitter from "../../../../../eventEmmiters/EventEmitter";
import axios from 'axios';
import FormElementGenerator from "../../form-elements/formElementGenerator";
import Loader from "../../../loader/loader";

/**
 * HOC for the Admin Role form
 */
class AdminRolesForm extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            adminRoleId: null,
            adminRoleVersion: 0,
            adminRoleAuthority: '',
            adminRoleDescription: '',
            isDisplayLoader: true,
            errors: {},

            token: '',
            fields: [
                {
                    inputType: 'text',
                    labelName: 'Authority *',
                    inputName: 'adminRoleAuthority',
                    placeholder: 'Please enter the authority here',
                    required: true,
                    disabled: false
                },
                {
                    inputType: 'text',
                    labelName: 'Description *',
                    inputName: 'adminRoleDescription',
                    placeholder: 'Please enter the description here',
                    required: true,
                    disabled: false
                }
            ]
        };

        // get the parameter from URL
        const adminRoleId = this.props.match.params.id;

        // set icons to the header
        if (adminRoleId === undefined) {
            EventEmitter.emit('setPageHeaderData', {
                page: 'admin-roles',
                pageName: "Admin Roles",
                pageLink: process.env.REACT_APP_BASE_URL + "/#/home/admin-roles",
                optionalIcons: ['backIcon','clearFormIcon']
            });
        } else {
            EventEmitter.emit('setPageHeaderData', {
                page: 'admin-roles',
                pageName: "Admin Roles",
                pageLink: process.env.REACT_APP_BASE_URL + "/#/home/admin-roles",
                optionalIcons: ['backIcon','clearFormIcon','deleteIcon']
            });
        }

        // set the event emitters
        EventEmitter.on('saveFormData', this.handleSubmit);
        EventEmitter.on('displayDeleteFormDataWarning', this.displayDeleteWarningMessage);
        EventEmitter.on('deleteFormData', this.deleteAdminRole);
        EventEmitter.on('navigateToPreviousPage', this.navigateToPreviousPage);
        EventEmitter.on('clearForm', this.clearForm);
    }

    // set a token to unsubscribe the async call
    CancelToken = axios.CancelToken;
    source = this.CancelToken.source();

    componentDidMount = async () => {
        // get parameter from URL
        const adminRoleId = this.props.match.params.id;

        // get token
        const user = JSON.parse(localStorage.getItem('tmcUser'));
        const token = 'Bearer ' + user.token;

        const oldState = Object.assign({}, this.state);

        // fill the form if not a create Admin Role
        if (adminRoleId !== undefined) {

            const url = '/admin-roles?search=%7BadminRoleId:' + adminRoleId + '%7D';

            await API.get(url, {headers: {Authorization: token}, cancelToken: this.source.token})
                .then(res => {
                    const data = res.data[0];
                    oldState.adminRoleId = (data.adminRoleId !== undefined) ? data.adminRoleId : null;
                    oldState.adminRoleAuthority = (data.adminRoleAuthority !== undefined) ? data.adminRoleAuthority : null;
                    oldState.adminRoleDescription = (data.adminRoleDescription !== undefined) ? data.adminRoleDescription : null;
                    oldState.token = token;
                    oldState.isDisplayLoader = false;

                    this.setState(oldState);
                }).catch(er => {
                    oldState.isDisplayLoader = false;
                    this.setState(oldState);
                    this.displayErrorsForAPICalls(er);
                });
        } else {
            oldState.token = token;
            oldState.isDisplayLoader = false;
            this.setState(oldState);
        }
    };

    componentWillUnmount = () => {
        // remove all event emitters
        EventEmitter.off('setPageHeaderData', {});
        EventEmitter.off('saveFormData', this.handleSubmit);
        EventEmitter.off('displayDeleteFormDataWarning', this.displayDeleteWarningMessage);
        EventEmitter.off('deleteFormData', this.deleteAdminRole);
        EventEmitter.off('navigateToPreviousPage', this.navigateToPreviousPage);
        EventEmitter.off('clearForm', this.clearForm);
        this.source.cancel("cancel admin roles async calls");
    };

    /**
     * set the state
     * @param event
     */
    handleChange = event => {
        if (event.optionValueName !== undefined) {
            this.setState({
                [event.optionValueName]: event.value,
                [event.optionLabelName]: event.label
            }, () => {
                this.validate(event.optionValueName, event.value)
            });
        } else {
            const key = event.currentTarget.name;
            const val = event.currentTarget.value;

            this.setState({
                [key]: val
            }, () => {
                this.validate(key, val)
            });
        }
    };

    /**
     * handle the form submission
     */
    handleSubmit = () => {
        // data to be sent
        let data = null;
        // check its a update or create
        if (this.state.adminRoleId !== null) {
            data = {
                adminRoleId: this.state.adminRoleId,
                adminRoleAuthority: this.state.adminRoleAuthority,
                adminRoleDescription: this.state.adminRoleDescription
            };
        } else {
            data = {
                adminRoleAuthority: this.state.adminRoleAuthority,
                adminRoleDescription: this.state.adminRoleDescription
            };
        }

        // validate each form field
        for (let [key, value] of Object.entries(data)) {
            this.validate(key, value);
        }

        // check weather there are error messages
        if (Object.values(this.state.errors).find(o => o !== '') === undefined) {
            // check update or create
            if (this.state.adminRoleId !== null) {
                this.updateAdminRoles(data);
            } else {
                this.createAdminRoles(data);
            }
        }
    };

    /**
     * validate the user inputs
     * @param key - input name
     * @param value - input value
     */
    validate = (key, value) => {
        let error = {};

        switch (key) {
            case 'adminRoleAuthority':
                if (value === '')
                    error[key] = "Authority is Required!";
                else if (value === ' ')
                    error[key] = "Authority can't be an empty space!";
                else if ((new TextEncoder().encode(value).length > 32))
                    error[key] = "Authority can't have more than 32 characters!";
                else
                    error[key] = "";
                break;
            case 'adminRoleDescription':
                if (value === '')
                    error[key] = "Description is Required!";
                else if (value === ' ')
                    error[key] = "Description can't be an empty space!";
                else if ((new TextEncoder().encode(value).length > 64))
                    error[key] = "Description can't have more than 64 characters!";
                else
                    error[key] = "";
                break;
            default:
                break;
        }

        const newState = Object.assign({}, this.state);
        newState.errors = Object.assign(newState.errors, error);

        this.setState(newState);
    };

    /**
     * create Admin Role
     * @param data - the data to create a new Admin Role
     */
    createAdminRoles = async (data) => {
        await API.post('/admin-roles',
            {
                "adminRoleAuthority": data.adminRoleAuthority,
                "adminRoleDescription": data.adminRoleDescription
            }, {headers: {Authorization: this.state.token}, cancelToken: this.source.token}
        ).then(res => {
            this.setAlert("Success", "Success", "Successfully Created the Admin Role");
            this.props.history.push('/home/admin-roles');
        }).catch(er => {
            this.displayErrorsForAPICalls(er);
        });
    };

    /**
     * update Admin Role
     * @param data - updated Admin Role details
     */
    updateAdminRoles = async (data) => {
        await API.put('/admin-roles',
            {
                "adminRoleId": data.adminRoleId,
                "adminRoleAuthority": data.adminRoleAuthority,
                "adminRoleDescription": data.adminRoleDescription
            },{headers: {Authorization: this.state.token}, cancelToken: this.source.token}
        ).then(response => {
            this.setAlert("Success", "Success", "Successfully updated the Admin Role");
            this.props.history.push('/home/admin-roles');
        }).catch(er => {
            this.displayErrorsForAPICalls(er);
        });
    };

    /**
     * display delete warning message
     */
    displayDeleteWarningMessage = () => {
        if (this.state.adminRoleId !== null) {
            EventEmitter.emit('warningModelData', {
                type: 'admin-roles',
                name: this.state.adminRoleAuthority
            });
            EventEmitter.emit('openWarningModel', true);
        } else {
            EventEmitter.emit('setAlert', {
                openAlert: true,
                type: "Error",
                isDisplayAlertIcon: true,
                alertMessage: "You are trying to delete before loading the Admin Role Details",
                alertTitle: "Error"
            });
        }

    };

    /**
     * delete a Admin Role
     * @param data
     */
    deleteAdminRole = async (data) => {
        if (data && (this.props.match.path === "/home/admin-roles/:id")) {
            await API.delete('/admin-roles?adminRoleId=' + this.state.adminRoleId, {headers: {Authorization: this.state.token}, cancelToken: this.source.token})
                .then(res => {
                    this.setAlert("Success", "Success", "Successfully Deleted the Admin Role");
                    this.props.history.push('/home/admin-roles');
                }).catch(er => {
                    this.displayErrorsForAPICalls(er)
                });
        }
    };

    displayErrorsForAPICalls = (error) => {
        if (error.response !== undefined && error.response.status === 404) {
            this.setAlert("Error", "Error", "The selected item not found!");
        } else if (error.message.includes('422')) {
            this.setAlert("Error", "Sorry", "Admin Role is already exist!");
        } else if (error.response === undefined || error.response.status === 401) {
            localStorage.removeItem('tmcUser')
            this.props.history.push('/');
        } else {
            this.setAlert("Error", "Error", "Something went wrong");
        }
    };

    /**
     * fire an alert
     * @param alertType
     * @param alertTitle
     * @param alertMessage
     */
    setAlert = (alertType, alertTitle, alertMessage) => {
        EventEmitter.emit('setAlert', {
            openAlert: true,
            type: alertType,
            isDisplayAlertIcon: true,
            alertMessage: alertMessage,
            alertTitle: alertTitle
        });
    };

    /**
     * clear the form
     */
    clearForm = () => {
        const newState = Object.assign({}, this.state);

        newState.adminRoleAuthority = "";
        newState.adminRoleDescription = "";
        newState.errors = {};

        this.setState(newState);
    };

    /**
     * navigate to previous page
     */
    navigateToPreviousPage = () => {
        this.props.history.push('/home/admin-roles');
    };

    render() {
        return(
            <div role="form" className="container">
                {this.state.isDisplayLoader && <Loader/>}
                <div className="main-btn-container">
                    <div className="buttons" tooltip="Save" onClick={this.handleSubmit}>
                        <i className="fa fa-save fa-lg icon-primary" />
                    </div>
                </div>
                <form>
                    <FormElementGenerator currentState={this.state} handleChange={this.handleChange} />
                </form>
            </div>
        )
    }
}

export default AdminRolesForm;
